import { Conversation, Message } from '../models/chat';
import { api } from '../providers/base-api';
import { chatDb } from './db';

export type PaginatedMessages = {
  data: Message[];
  nextCursor: number | null;
};

export const chatService = {
  createPrivateConversation: async (userBId: number): Promise<Conversation> => {
    const { data } = await api.post(`/chat/private/${userBId}`);
    return data;
  },

  createGroupConversation: async (
    name: string,
    participantIds: number[],
  ): Promise<Conversation> => {
    const { data } = await api.post('/chat/group', {
      name,
      participantIds,
    });
    return data;
  },

  listMessages: async (
    conversationId: number,
    ownerId: number,
    cursor?: number,
    limit?: number,
    persist = true,
  ): Promise<PaginatedMessages> => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', String(cursor));
    if (limit) params.set('limit', String(limit));

    const query = params.toString();
    const url = `/chat/messages/${conversationId}${query ? `?${query}` : ''}`;
    const { data } = await api.get(url);

    if (persist && data.data?.length) {
      chatDb.messages
        .bulkPut(
          data.data.map((m: any) => ({
            ...m,
            username: m.username || m.sender?.username,
            ownerId,
          })),
        )
        .catch(console.error);
    }

    return data;
  },

  sendMessage: async (
    conversationId: number,
    message: string,
    ownerId: number,
    attachment?: { url: string; name: string; type: string },
  ): Promise<Message> => {
    const { data } = await api.post('/chat/send/message', {
      conversationId,
      message,
      ...(attachment && {
        attachmentUrl: attachment.url,
        attachmentName: attachment.name,
        attachmentType: attachment.type,
      }),
    });

    chatDb.messages.put({ ...data, ownerId }).catch(console.error);

    return data;
  },

  listConversations: async (ownerId: number): Promise<Conversation[]> => {
    const { data } = await api.get('/chat/conversations');
    if (data?.length) {
      chatDb.conversations
        .bulkPut(data.map((c: any) => ({ ...c, ownerId })))
        .catch(console.error);
    }
    return data;
  },

  listAllConversations: async (): Promise<Conversation[]> => {
    const { data } = await api.get('/chat/admin/conversations');
    // Monitor view: do NOT persist monitored conversations to personal local repository
    return data;
  },

  markAsRead: async (conversationId: number): Promise<void> => {
    await api.post(`/chat/messages/${conversationId}/read`);
  },

  listUsers: async (): Promise<any[]> => {
    const [masters, vendors, sac] = await Promise.all([
      api.get('/users/by-role/other'),
      api.get('/users/by-role/vendor'),
      api.get('/users/by-role/sac'),
    ]);
    return [...masters.data, ...vendors.data, ...sac.data];
  },
};
